import { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useHelp } from './HelpContext'
import { CHECKLIST, PAGE_TOURS, TIPS, VIDEOS, WELCOME_TOUR } from './content'
import { routeOf } from './dom'
import './help.css'

// "?" in the top bar: replay this page's tour, reread its tips, open Getting
// started, and turn automatic help off or back on.
function HelpMenu() {
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const route = routeOf(pathname)
  const { steps, off, setOff, reset, startTour } = useHelp()
  const [open, setOpen] = useState(false)
  const [expanded, setExpanded] = useState(null)
  const [note, setNote] = useState('')
  const ref = useRef(null)

  useEffect(() => {
    if (!open) return undefined
    const onDown = (event) => {
      if (ref.current && !ref.current.contains(event.target)) setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [open])

  useEffect(() => {
    if (open) return
    setExpanded(null)
    setNote('')
  }, [open])

  const tour = PAGE_TOURS[route]
  const tips = TIPS.filter((tip) => tip.route === route || !tip.route)
  const video = VIDEOS[route]
  const done = CHECKLIST.filter((step) => steps.has(step.id)).length

  const runTour = () => {
    if (startTour(tour, route)) setOpen(false)
    else setNote('This page is still loading. Try again in a moment.')
  }

  const runWelcome = () => {
    if (startTour(WELCOME_TOUR, route)) setOpen(false)
    else setNote('This page is still loading. Try again in a moment.')
  }

  return (
    <div className="help-menu" ref={ref}>
      <button
        type="button"
        className="help-menu-btn"
        title="Help"
        aria-label="Help"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        ?
      </button>
      {open ? (
        <div className="help-dropdown" role="menu">
          <button type="button" className="help-item" role="menuitem" onClick={runWelcome}>
            Replay the welcome tour
          </button>
          {tour ? (
            <button type="button" className="help-item" role="menuitem" onClick={runTour}>
              Tour this page
            </button>
          ) : null}
          <button
            type="button"
            className="help-item"
            role="menuitem"
            onClick={() => {
              setOpen(false)
              navigate('/home')
            }}
          >
            Getting started
            <span className="help-item-meta">
              {done} of {CHECKLIST.length}
            </span>
          </button>
          {video ? (
            <a className="help-item" role="menuitem" href={video} target="_blank" rel="noopener noreferrer">
              Watch a video
            </a>
          ) : null}
          {note ? <div className="help-tip">{note}</div> : null}

          {tips.length ? (
            <>
              <div className="help-divider" />
              <div className="help-section">Tips</div>
              {tips.map((tip) => (
                <div key={tip.id}>
                  <button
                    type="button"
                    className="help-item"
                    aria-expanded={expanded === tip.id}
                    onClick={() => setExpanded((current) => (current === tip.id ? null : tip.id))}
                  >
                    <span className="help-caret">{expanded === tip.id ? '▾' : '▸'}</span>
                    {tip.title}
                  </button>
                  {expanded === tip.id ? <div className="help-tip">{tip.body}</div> : null}
                </div>
              ))}
            </>
          ) : null}

          <div className="help-divider" />
          <button type="button" className="help-item" role="menuitem" onClick={() => setOff(!off)}>
            {off ? 'Turn on automatic help' : 'Turn off automatic help'}
          </button>
          <button
            type="button"
            className="help-item"
            role="menuitem"
            onClick={() => {
              setOpen(false)
              reset().catch(() => {})
            }}
          >
            Show all tours and tips again
          </button>
        </div>
      ) : null}
    </div>
  )
}

export default HelpMenu
